import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import Card from 'dashboard/components/card/Card.js';
import { SearchBar } from 'dashboard/components/navbar/searchBar/SearchBar';
import StagePopup from 'dashboard/components/tableList/actions';

/**
 * Owner-facing generic table with column-driven cells, search, edit /
 * delete buttons, and react-table pagination. Used by the IAM stages.
 */
function TableManagement(props) {
  // The save handlers belong to whoever owns the data — this table is
  // generic. They were not being forwarded to the dialog at all, so Submit
  // called `undefined` and threw: nothing was ever saved, and the only sign
  // was an error in the console.
  // Renamed on the way in: this component already has handleEdit/handleDelete
  // of its own, which open the dialog on a row. These are the callbacks that
  // actually write, and they belong to whoever owns the data.
  const {
    columnsData, tableData, name, popupModal, selectedBrand, getPageData,
    handleCreate: onCreateEntry,
    handleEdit: onSaveEntry,
    handleDelete: onDeleteEntry,
  } = props;
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const [openDialog, setOpenDialog] = useState(false);
  const [entryData, setEntryData] = useState({});

  const tableInstance = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    pageCount,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
    setPageSize,
    state,
  } = tableInstance;

  const { pageIndex, pageSize } = state;

  const createPages = (count) => Array.from({ length: count }, (_, i) => i + 1);

  const handleEdit = (row) => { setEntryData({ data: row, mode: 'edit' }); setOpenDialog(true); };
  const handleDelete = (row) => { setEntryData({ data: row, mode: 'delete' }); setOpenDialog(true); };

  return (
    <>
      <Card>
        <Stack direction='column' width='100%'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            width='100%'
            flexWrap='wrap'
            gap='10px'
            sx={{ px: '20px', mb: '20px' }}
          >
            {/* No heading here. The bar at the top of the screen already
                names what you are looking at, and this repeated it directly
                underneath — the same word twice, forty pixels apart.
                The search field takes the row, so on a phone it is full width
                rather than squeezed into what is left beside Add. */}
            <SearchBar
              onChange={(e) => setGlobalFilter(e.target.value)}
              sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 }, maxWidth: { lg: '390px' } }}
            />
            <IconButton color='primary' onClick={() => setOpenDialog(true)}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Stack>

          {/* The table is the one thing on these screens that cannot be made
              to fit a phone — a content entry has a name, a status, dates and
              its actions, and dropping columns loses information people came
              for. So it scrolls sideways inside its own box, which is the
              one place that is allowed to, instead of making the whole page
              scroll sideways underneath the fixed top bar. */}
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup) => {
                const { key: hgKey, ...hgRest } = headerGroup.getHeaderGroupProps();
                return (
                  <TableRow key={hgKey} {...hgRest}>
                    {headerGroup.headers.map((column) => {
                      const { key: cKey, ...cRest } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <TableCell key={cKey} {...cRest} sx={{ pr: '10px' }}>
                          <Typography variant='caption' color='text.secondary' fontWeight='600'>
                            {column.render('Header')}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                const { key: rKey, ...rRest } = row.getRowProps();
                return (
                  <TableRow key={rKey} {...rRest}>
                    {row.cells.map((cell) => {
                      let content = null;
                      const header = cell.column.Header;
                      const headerLower = header?.toLowerCase?.() || '';
                      if (header === 'ACTIONS') {
                        content = (
                          <Stack direction='row' spacing='8px'>
                            <IconButton size='small' color='primary' onClick={() => handleEdit(cell.row.original)}>
                              <EditIcon fontSize='small' />
                            </IconButton>
                            <IconButton size='small' color='primary' onClick={() => handleDelete(cell.row.original)}>
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </Stack>
                        );
                      } else if (headerLower === 'published' || headerLower === 'status order') {
                        content = (
                          <Chip
                            size='small'
                            color={cell.value ? 'success' : 'error'}
                            label={cell.value ? 'Published' : 'Draft'}
                          />
                        );
                      } else if (headerLower === 'image') {
                        content = (
                          <Avatar src={cell.value} sx={{ width: 32, height: 32, mr: '16px' }} />
                        );
                      } else {
                        content = (
                          <Typography variant='body2' fontWeight='600'>
                            {cell.value}
                          </Typography>
                        );
                      }

                      const { key: cKey, ...cRest } = cell.getCellProps();
                      return (
                        <TableCell
                          key={cKey}
                          {...cRest}
                          sx={{ fontSize: { xs: '14px' }, minWidth: { xs: '120px', md: '200px', lg: 'auto' } }}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', md: 'center' }}
            width='100%'
            sx={{ mt: '20px', px: { md: '22px' } }}
          >
            <Stack direction='row' alignItems='center' spacing='10px'>
              <Typography variant='body2' color='text.secondary'>
                Show rows per page
              </Typography>
              <FormControl size='small' sx={{ minWidth: 70 }}>
                <InputLabel>Rows</InputLabel>
                <Select
                  label='Rows'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[6, 7, 8, 9, 10].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction='row' spacing='4px' alignItems='center'>
              {canPreviousPage && (
                <Button
                  onClick={previousPage}
                  variant='outlined'
                  sx={{ width: 40, height: 40, borderRadius: '50%', minWidth: 'unset' }}
                >
                  <ChevronLeftIcon fontSize='small' />
                </Button>
              )}
              {createPages(pageCount).map((pageNumber) => (
                <Button
                  key={pageNumber}
                  onClick={() => gotoPage(pageNumber - 1)}
                  variant={pageNumber === pageIndex + 1 ? 'contained' : 'outlined'}
                  sx={{ width: 40, height: 40, borderRadius: '50%', minWidth: 'unset' }}
                >
                  <Typography variant='body2'>{pageNumber}</Typography>
                </Button>
              ))}
              {canNextPage && (
                <Button
                  onClick={nextPage}
                  variant='outlined'
                  sx={{ width: 40, height: 40, borderRadius: '50%', minWidth: 'unset' }}
                >
                  <ChevronRightIcon fontSize='small' />
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Card>
      {/* Not gated on the field list having loaded. It used to be, so when
          that call failed the Add button did nothing whatsoever — no dialog,
          no error, nothing to act on. The dialog now opens and says so. */}
      {openDialog && (
        <StagePopup
          entryData={entryData}
          getPageData={getPageData}
          selectedBrand={selectedBrand}
          popupModal={popupModal}
          name={name}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          handleCreate={onCreateEntry}
          handleEdit={onSaveEntry}
          handleDelete={onDeleteEntry}
          key={name}
        />
      )}
    </>
  );
}

export default TableManagement;
